import { Component, DestroyRef, EventEmitter, inject, Input, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ModalComponent } from '../../../ui/modal-component/modal-component.component';
import { FormWrapperComponent } from '../../../ui/form-wrapper/form-wrapper.component';
import { StatusComponent } from '../../../ui/status/status.component';
import { EditScheduleComponent } from './edit-schedule/edit-schedule.component';
import { PromptCardComponent } from '../../../toasts-and-cards/prompt-card/prompt-card.component';
import { EngagementMessageSchedule } from '../../../../pages/organization/campaigns/types/messagesTypes';
import { formatCountdown, formatWhen } from '../../../../../utils/sms';
import {
  isStopped,
  scheduleMoment,
  schedulesOf,
  subjectOf,
} from '../../../../pages/organization/campaigns/campaign-details/campaign-sends';
import {
  CampaignDetails,
  EngagementMessage,
} from '../../../../pages/organization/campaigns/types/campaignDetails';
import { CampaignsService } from '../../../../services/campaign/campaigns-api.service';
import { CustomizableButtonComponent } from '../../../ui/customizable-button/customizable-button.component';

@Component({
  selector: 'app-message-schedules',
  standalone: true,
  imports: [
    MatTooltipModule,
    ModalComponent,
    FormWrapperComponent,
    StatusComponent,
    EditScheduleComponent,
    PromptCardComponent,
    CustomizableButtonComponent,
  ],
  templateUrl: './message-schedules.component.html',
  styleUrl: '../campaign-form.css',
})
export class MessageSchedulesComponent {
  @Output() onClose = new EventEmitter();
  @Input({ required: true }) openModal = false;
  @Input() message!: EngagementMessage;
  campaignsService = inject(CampaignsService);
  private destroyRef = inject(DestroyRef);
  campaign!: CampaignDetails;
  scheduleToEditData: any = {};
  scheduleToDeleteId: number | null = null;

  editSchedule = false;
  deleteSchedule = false;

  editingSchedule(id: number) {
    // set schedule data passed to edit component
    this.scheduleToEditData = this.message.engagementMessageSchedules.find(
      (item) => item.id === id
    ) as EngagementMessageSchedule;
    this.editSchedule = true;
  }

  addSchedule() {
    // clear schedule data passed to edit schedule component so that component is not prepopulated
    this.scheduleToEditData = {};
    this.editSchedule = true;
  }

  get subject(): string {
    return subjectOf(this.message);
  }

  /** The message's schedules, soonest first, in the viewer's time. */
  get rows() {
    const now = new Date();
    return schedulesOf(this.message)
      .map((schedule, index) => {
        const at = scheduleMoment(schedule);
        const past = !!at && at.getTime() <= now.getTime();
        const stopped = isStopped(schedule.status);
        const zone = schedule.scheduleTimeZone ?? '';
        return {
          key: schedule.id ?? `s-${index}`,
          id: schedule.id,
          at: at?.getTime() ?? Infinity,
          when: formatWhen(at, now),
          exact: [schedule.scheduleDate?.slice(0, 10), schedule.scheduleTime?.slice(0, 5), zone]
            .filter(Boolean)
            .join(' '),
          sub: [past || stopped ? '' : formatCountdown(at, now), zone].filter(Boolean).join(' · '),
          status: String(schedule.status ?? ''),
        };
      })
      .sort((a, b) => a.at - b.at);
  }

  getScheduleToDelete(id: number) {
    this.deleteSchedule = true;
    this.scheduleToDeleteId = id;
  }

  deleteScheduleItem(id: number | null) {
    if (id) {
      const data = {
        ...this.campaign,
        engagementMessages: this.campaign.engagementMessages.map((item) => {
          if (item.id === this.message.id) {
            return {
              ...item,
              engagementMessageSchedules:
                item.engagementMessageSchedules.filter(
                  (schedule) => schedule.id !== id
                ),
            };
          }
          return item;
        }),
      };

      this.campaignsService
        .editCampaign(this.campaign.id, data, false)
        .subscribe(() => {
          this.deleteSchedule = false;
        });
    }
  }

  ngOnInit() {
    //assign data to component campaign variable
    this.campaignsService.campaignDetails
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
      this.campaign = data as CampaignDetails;
    });
  }
}
